import type { GovernanceVoting, ValidatorSnapshot } from '@/types/governance';
import type { ActiveValidator } from '@/lib/suiClient';
import mockDescriptionContent from './mockDescription.md?raw';

// Helper function to get timestamps relative to current time
function getTimestamps(scenario: string) {
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const twoDays = 2 * oneDay;
    const sevenDays = 7 * oneDay;
    let start_timestamp = now + oneDay;

    switch (scenario) {
        case 'mock_notstarted':
            start_timestamp = now + oneDay;
            return {
                start_timestamp_ms: start_timestamp,
                minimum_duration_timestamp_ms: start_timestamp + twoDays, // 48h after start
                end_timestamp_ms: start_timestamp + sevenDays
            };
        case 'mock_beforecutoff':
        case 'mock_aftercutoff': // These are now the same since there's no cutoff
        case 'mock_passing':
        case 'mock_notpassingbutyes':
        case 'mock_notpassingbutno':
            start_timestamp = now - oneDay;
            return {
                start_timestamp_ms: start_timestamp,
                minimum_duration_timestamp_ms: start_timestamp + twoDays, // 48h after start
                end_timestamp_ms: start_timestamp + sevenDays
            };
        case 'mock_early_passing_voting':
        case 'mock_early_passing_staking': // These are now the same since there's no phases
            // Started 3 days ago (72 hours), minimum duration has passed
            start_timestamp = now - (3 * oneDay);
            return {
                start_timestamp_ms: start_timestamp,
                minimum_duration_timestamp_ms: start_timestamp + twoDays, // 48h after start (already passed)
                end_timestamp_ms: start_timestamp + sevenDays
            };
        case 'mock_ended':
            start_timestamp = now - sevenDays - oneDay;
            return {
                start_timestamp_ms: start_timestamp,
                minimum_duration_timestamp_ms: start_timestamp + twoDays,
                end_timestamp_ms: start_timestamp + sevenDays
            };
        default:
            start_timestamp = now - oneDay;
            return {
                start_timestamp_ms: start_timestamp,
                minimum_duration_timestamp_ms: start_timestamp + twoDays,
                end_timestamp_ms: start_timestamp + sevenDays
            };
    }
}

// Helper function to generate vote distribution based on scenario
function getVoteDistribution(scenario: string, totalValidators: number) {
    const participationRates = {
        mock_notstarted: 0, // No votes yet
        mock_beforecutoff: 0.6, // 60% participation during voting
        mock_aftercutoff: 0.6, // Same as beforecutoff since there's no cutoff
        mock_passing: 0.8, // High participation, passing
        mock_notpassingbutyes: 0.4, // Low participation, yes majority but insufficient turnout
        mock_notpassingbutno: 0.7, // High participation but no majority
        mock_early_passing_voting: 0.75, // High participation, early pass conditions met
        mock_early_passing_staking: 0.8, // High participation, passed early
        mock_ended: 0.75 // Final results
    };

    // Vote distribution: [yesRate, noRate, abstainRate] - should sum to 1.0
    const voteDistributions = {
        mock_notstarted: [0, 0, 0],
        mock_beforecutoff: [0.5, 0.3, 0.2], // 50% yes, 30% no, 20% abstain
        mock_aftercutoff: [0.5, 0.3, 0.2], // Same as beforecutoff
        mock_passing: [0.6, 0.25, 0.15], // 60% yes, 25% no, 15% abstain - should pass (yes > no && turnout > 50%)
        mock_notpassingbutyes: [0.7, 0.2, 0.1], // 70% yes, 20% no, 10% abstain but low turnout
        mock_notpassingbutno: [0.35, 0.45, 0.2], // 35% yes, 45% no, 20% abstain - no wins
        mock_early_passing_voting: [0.7, 0.15, 0.15], // 70% yes, 15% no, 15% abstain - early pass
        mock_early_passing_staking: [0.75, 0.15, 0.1], // 75% yes, 15% no, 10% abstain - passed early
        mock_ended: [0.58, 0.27, 0.15]
    };

    const participation = participationRates[scenario as keyof typeof participationRates];
    const [yesRate, noRate, abstainRate] = voteDistributions[scenario as keyof typeof voteDistributions];

    const votingValidators = Math.floor(totalValidators * participation);
    const yesVotes = Math.floor(votingValidators * yesRate);
    const noVotes = Math.floor(votingValidators * noRate);
    const abstainVotes = votingValidators - yesVotes - noVotes; // Remaining go to abstain

    return { votingValidators, yesVotes, noVotes, abstainVotes };
}

// Function to read the mock description from the markdown file
async function getMockDescription(): Promise<string> {
    try {
        const response = await fetch('/src/data/mockDescription.md');
        return await response.text();
    } catch (error) {
        console.error('Failed to load mock description:', error);
        // Fallback description
        return '# Protocol Upgrade v1.15.0 - Enhanced Consensus Mechanism\n\nThis proposal introduces significant improvements to the Sui blockchain protocol.';
    }
}

// Base mock data template
const baseMockVote: Omit<GovernanceVoting, 'start_timestamp_ms' | 'minimum_duration_timestamp_ms' | 'end_timestamp_ms' | 'votes'> = {
    id: "0x123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef01",
    result: "Pending",
    description: mockDescriptionContent,
    digests: [
        "0xabc123def456789abc123def456789abc123def456789abc123def456789abc123",
        "0xdef456789abc123def456789abc123def456789abc123def456789abc123def456"
    ]
};

// Mock active validators with names for development - ENSURE STAKE IS BIGINT
// Moved this definition before its usage in legacy exports
export const mockActiveValidators: ActiveValidator[] = [
    { address: "0x1a2b3c4d5e6f7890abcdef1234567890abcdef1234567890abcdef1234567890", stake: 15000000000000000n, name: "Mysten Labs" },
    { address: "0x2b3c4d5e6f7890ab1234567890abcdef1234567890abcdef1234567890abcdef", stake: 12000000000000000n, name: "Sui Foundation" },
    { address: "0x3c4d5e6f7890ab121234567890abcdef1234567890abcdef1234567890abcdef", stake: 8000000000000000n, name: "Coinbase Cloud" },
    { address: "0x4d5e6f7890ab12341234567890abcdef1234567890abcdef1234567890abcdef", stake: 10000000000000000n, name: "Binance Staking" },
    { address: "0x5e6f7890ab123456234567890abcdef1234567890abcdef1234567890abcdef1", stake: 6000000000000000n, name: "Kraken" },
    { address: "0x6f7890ab12345678234567890abcdef1234567890abcdef1234567890abcdef12", stake: 9000000000000000n, name: "OKX Pool" },
    { address: "0x7890ab1234567890234567890abcdef1234567890abcdef1234567890abcdef123", stake: 7000000000000000n, name: "Figment" },
    { address: "0x890ab12345678901234567890abcdef1234567890abcdef1234567890abcdef1234", stake: 11000000000000000n, name: "Chorus One" },
    { address: "0x90ab123456789012234567890abcdef1234567890abcdef1234567890abcdef12345", stake: 13000000000000000n, name: "Staking Facilities" },
    { address: "0x0ab1234567890123234567890abcdef1234567890abcdef1234567890abcdef123456", stake: 5000000000000000n, name: "P2P Validator" },
    { address: "0xab1234567890123a234567890abcdef1234567890abcdef1234567890abcdef123456", stake: 4000000000000000n, name: "Blockdaemon" },
    { address: "0xb1234567890123ab234567890abcdef1234567890abcdef1234567890abcdef123456", stake: 3000000000000000n, name: "Everstake" },
];

// Generate mock governance vote based on scenario
export function generateMockGovernanceVote(scenario: string, validators: ActiveValidator[] = []): GovernanceVoting {
    const timestamps = getTimestamps(scenario);
    const { votingValidators, yesVotes, noVotes, abstainVotes } = getVoteDistribution(scenario, validators.length || 12);

    // Use real validators if available, otherwise use mock addresses
    const validatorAddresses = validators.length > 0
        ? validators.map(v => v.address)
        : [
            "0x1a2b3c4d5e6f7890abcdef1234567890abcdef1234567890abcdef1234567890",
            "0x2b3c4d5e6f7890ab1234567890abcdef1234567890abcdef1234567890abcdef",
            "0x3c4d5e6f7890ab121234567890abcdef1234567890abcdef1234567890abcdef",
            "0x4d5e6f7890ab12341234567890abcdef1234567890abcdef1234567890abcdef",
            "0x5e6f7890ab123456234567890abcdef1234567890abcdef1234567890abcdef1",
            "0x6f7890ab12345678234567890abcdef1234567890abcdef1234567890abcdef12",
            "0x7890ab1234567890234567890abcdef1234567890abcdef1234567890abcdef123",
            "0x890ab12345678901234567890abcdef1234567890abcdef1234567890abcdef1234",
            "0x90ab123456789012234567890abcdef1234567890abcdef1234567890abcdef12345",
            "0x0ab1234567890123234567890abcdef1234567890abcdef1234567890abcdef123456",
            "0xab1234567890123a234567890abcdef1234567890abcdef1234567890abcdef123456",
            "0xb1234567890123ab234567890abcdef1234567890abcdef1234567890abcdef123456"
        ];

    // Generate votes based on distribution
    const votes: Record<string, 'Yes' | 'No' | 'Abstain'> = {};
    for (let i = 0; i < votingValidators; i++) {
        let voteValue: "Yes" | "No" | "Abstain";

        if (i < yesVotes) {
            voteValue = "Yes";
        } else if (i < yesVotes + noVotes) {
            voteValue = "No";
        } else {
            voteValue = "Abstain";
        }
        // Assuming validatorAddresses[i] is defined
        if (validatorAddresses[i]) {
            votes[validatorAddresses[i]] = voteValue;
        }
    }

    // Determine result based on scenario
    let result: "Pending" | "Passed" | "NotPassed" = "Pending";
    if (scenario === 'mock_early_passing_voting' || scenario === 'mock_early_passing_staking') {
        result = "Passed"; // Mark as passed early
    } else if (scenario === 'mock_ended') {
        result = "Passed"; // Assume ended scenario passed
    }

    return {
        ...baseMockVote,
        ...timestamps,
        votes,
        result
    };
}

// Generate mock validator snapshot based on scenario and validators
export function generateMockValidatorSnapshot(scenario: string, validators: ActiveValidator[] = []): ValidatorSnapshot {
    // ActiveValidator from the hook now guarantees stake is bigint.
    // If validators array is empty, it uses mockActiveValidators which also has stake as bigint.
    const validatorDataSource: ReadonlyArray<ActiveValidator> = validators.length > 0 ? validators : mockActiveValidators;

    const all_stake: Record<string, bigint> = {};
    validatorDataSource.forEach((validator, index) => {
        // validator.stake is now guaranteed to be bigint if validator object is complete.
        // The fallback BigInt calculation is for a hypothetical incomplete ActiveValidator from a source,
        // but with current setup, validator.stake should be reliable.
        all_stake[validator.address] = validator.stake; // Directly assign, as it should be bigint
    });

    const ignored_stake: Record<string, bigint> = {};
    if (validatorDataSource[0]?.address) {
        ignored_stake[validatorDataSource[0].address] = 1000000000000000n;
    }
    if (validatorDataSource[1]?.address) {
        ignored_stake[validatorDataSource[1].address] = 500000000000000n;
    }

    return {
        id: "0x987654321fedcba0987654321fedcba0987654321fedcba0987654321fedcba09",
        all_stake,
        ignored_stake
    };
}

// Legacy exports for backward compatibility - ensure these also use new types
export const mockGovernanceVote: GovernanceVoting = generateMockGovernanceVote('mock_beforecutoff', mockActiveValidators);
export const mockValidatorSnapshot: ValidatorSnapshot = generateMockValidatorSnapshot('mock_beforecutoff', mockActiveValidators); 